import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import api from "../../Utils/api";
import { toast } from "react-toastify";

// Dummy data for dropdowns

const settingsOptions = ["Operating hours"];
// const settingsOptions = ["Operating hours", "Scheduling", "Timesheets"];

function MultiSelectDropdown({ options, selected, setSelected, label }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle click outside
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const allSelected = selected.length === options.length;
  const toggleOption = (opt) => {
    setSelected(
      selected.some((item) => item.id === opt.id)
        ? selected.filter((o) => o.id !== opt.id)
        : [...selected, opt]
    );
  };

  const toggleAll = () => {
    setSelected(allSelected ? [] : [...options]);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className="border rounded px-3 py-2 bg-white flex items-center cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex-1 flex  items-center">
          {selected.length > 0 && (
            <span className="bg-gray-100 !w-28 whitespace-nowrap text-ellipsis overflow-hidden rounded-md p-1 ">
              {selected.length === 0
                ? ""
                : selected.length === 1
                ? selected[0].name
                : `${selected[0].name} `}
            </span>
          )}
          {selected.length > 1 && (
            <span className="bg-gray-100 text-gray-700 p-1 rounded-md ml-1 ">
              + {selected.length - 1}
            </span>
          )}
        </span>
        <svg
          className="w-4 h-4 text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      {open && (
        <div className="absolute left-0 mt-2 w-full bg-white rounded shadow-lg z-10 p-2">
          <label className="flex items-center px-2 py-1 cursor-pointer font-medium text-gray-700">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="mr-2"
            />
            Select all ({options.length})
          </label>
          <div className="divide-y">
            {options.map((opt) => (
              <label
                key={opt.id}
                className="flex items-center px-2 py-1 cursor-pointer text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={selected.some((item) => item.id === opt.id)}
                  onChange={() => toggleOption(opt)}
                  className="mr-2"
                />
                {opt.name}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CopySettingsDialog({ open, onClose, locations }) {
  const [from, setFrom] = useState(locations[0].id);
  const [to, setTo] = useState([]);
  const [settings, setSettings] = useState([]);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    setOptions(locations.filter((loc) => loc.id != from));
  }, [from]);

  if (!open) return null;

  const copySettings = async () => {
    let operatingHours = false;
    let scheduling = false;
    let timesheets = false;

    if (settings.some((item) => item == "Operating hours"))
      operatingHours = true;
    if (settings.some((item) => item == "Scheduling")) scheduling = true;
    if (settings.some((item) => item == "Timesheets")) timesheets = true;

    if (operatingHours == false && scheduling == false && timesheets == false)
      toast.info("Select at least one setting to copy");

    let targetLocationID = from;
    let newLocations = to.map((item) => item.id);

    const requestBody = {
      targetLocationID,
      newLocations,
      operatingHours,
      scheduling,
      timesheets,
    };

    const res = await api.post("/location/copy_settings", requestBody);

    if (res.data.success) {
      toast.success(res.data.message);
    } else {
      toast.error(res.data.message);
    }

    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 relative">
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-2xl text-gray-800 hover:text-gray-900"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          Copy settings
        </h2>
        <div className="mb-4 flex gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From
            </label>
            <select
              className="w-full border rounded px-3 py-2"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            >
              {locations.map((opt) => (
                <option key={opt.id} value={opt.name}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To
            </label>
            <MultiSelectDropdown
              options={options}
              selected={to}
              setSelected={setTo}
              label="To"
            />
          </div>
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Settings
          </label>
          <div className="space-y-2">
            {settingsOptions.map((opt) => (
              <label key={opt} className="flex items-center text-gray-700">
                <input
                  type="checkbox"
                  checked={settings.includes(opt)}
                  onChange={() =>
                    setSettings(
                      settings.includes(opt)
                        ? settings.filter((o) => o !== opt)
                        : [...settings, opt]
                    )
                  }
                  className="mr-2"
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            className="bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-800 focus:outline-none"
            onClick={copySettings}
          >
            Copy settings
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
